/**
 * Select Options Utilities
 *
 * Centralized location for all select element options used across the application.
 * This prevents duplication and ensures consistency.
 */

import { z } from '../validation/zod';

import type {
  EquipmentAccess,
  FitnessChallenge,
  FitnessGoal,
  FitnessLevel,
  Gender,
  GoalTimeline,
  Motivation,
  TimeCommitment,
  WorkoutFrequency,
  WorkoutLocation,
  WorkoutType,
} from '../types';
import type { ExercisePreferenceTrackingType } from '../types/domain/user-preferences.types';
import type { TrackingType } from '../validation/common.schemas';

// =============================================================================
// ROLE OPTIONS
// =============================================================================

export const ROLE_OPTIONS = [
  { value: 'client', label: 'Client' },
  { value: 'trainer', label: 'Trainer' },
] as const;

// =============================================================================
// GENDER OPTIONS
// =============================================================================

/**
 * Gender options for registration (excludes 'prefer_not_to_say')
 */
export const GENDER_OPTIONS = [
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
  { value: 'female', label: 'Female' },
  { value: 'male', label: 'Male' },
  { value: 'non_binary', label: 'Non-binary' },
  { value: 'transgender', label: 'Transgender' },
  { value: 'genderqueer', label: 'Genderqueer' },
  { value: 'agender', label: 'Agender' },
  { value: 'bigender', label: 'Bigender' },
  { value: 'gender_fluid', label: 'Gender Fluid' },
] as const;

/**
 * Gender options for profile updates (includes 'prefer_not_to_say')
 */
export const FULL_GENDER_OPTIONS = [
  { value: '', label: 'Select gender (optional)' },
  { value: 'female', label: 'Female' },
  { value: 'male', label: 'Male' },
  { value: 'non_binary', label: 'Non-binary' },
  { value: 'transgender', label: 'Transgender' },
  { value: 'genderqueer', label: 'Genderqueer' },
  { value: 'agender', label: 'Agender' },
  { value: 'bigender', label: 'Bigender' },
  { value: 'gender_fluid', label: 'Gender Fluid' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
] as const;

/**
 * The genders a profile can hold: `FULL_GENDER_OPTIONS` without its
 * placeholder row. The Account panels list these, on web and mobile.
 */
export const PROFILE_GENDER_OPTIONS: SelectOption[] =
  FULL_GENDER_OPTIONS.filter((option) => option.value !== '');

// =============================================================================
// BODY MEASUREMENT OPTIONS
// =============================================================================

/**
 * Body measurement options for logging progress
 */
export const BODY_MEASUREMENTS = [
  { label: 'Weight', value: 'weight' },
  { label: 'Height', value: 'height' },
  { label: 'Body Fat Percentage', value: 'body_fat_percentage' },
  { label: 'Body Mass Index', value: 'body_mass_index' },
  { label: 'Muscle Mass', value: 'muscle_mass' },
  { label: 'Bone Mass', value: 'bone_mass' },
  { label: 'Water Percentage', value: 'water_percentage' },
  { label: 'Visceral Fat', value: 'visceral_fat' },
  { label: 'Lean Body Mass', value: 'lean_body_mass' },
  { label: 'Fat-Free Mass', value: 'fat_free_mass' },
  { label: 'Skeletal Muscle Mass', value: 'skeletal_muscle_mass' },
  { label: 'Body Fat Mass', value: 'body_fat_mass' },
  { label: 'Metabolic Age', value: 'metabolic_age' },
  { label: 'Basal Metabolic Rate (BMR)', value: 'basal_metabolic_rate_bmr' },
  { label: 'Daily Calorie Needs', value: 'daily_calorie_needs' },
  { label: 'Waist Circumference', value: 'waist_circumference' },
  { label: 'Hip Circumference', value: 'hip_circumference' },
  { label: 'Chest Circumference', value: 'chest_circumference' },
  { label: 'Neck Circumference', value: 'neck_circumference' },
  { label: 'Thigh Circumference', value: 'thigh_circumference' },
  { label: 'Calf Circumference', value: 'calf_circumference' },
  { label: 'Upper Arm Circumference', value: 'upper_arm_circumference' },
  { label: 'Bicep Circumference', value: 'bicep_circumference' },
  { label: 'Forearm Circumference', value: 'forearm_circumference' },
  { label: 'Shoulder Circumference', value: 'shoulder_circumference' },
  { label: 'Ankle Circumference', value: 'ankle_circumference' },
  { label: 'Wrist Circumference', value: 'wrist_circumference' },
  { label: 'Resting Heart Rate', value: 'resting_heart_rate' },
  { label: 'Blood Pressure (Systolic)', value: 'blood_pressure_systolic' },
  { label: 'Blood Pressure (Diastolic)', value: 'blood_pressure_diastolic' },
  { label: 'VO2 Max', value: 'vo2_max' },
  { label: 'Recovery Heart Rate', value: 'recovery_heart_rate' },
  {
    label: 'Heart Rate Variability (HRV)',
    value: 'heart_rate_variability_hrv',
  },
  { label: 'Grip Strength', value: 'grip_strength' },
  { label: 'Flexibility Score', value: 'flexibility_score' },
  { label: 'Balance Score', value: 'balance_score' },
  { label: 'Vertical Jump', value: 'vertical_jump' },
  { label: 'Push-ups (Max)', value: 'push_ups_max' },
  { label: 'Plank Hold Time', value: 'plank_hold_time' },
  { label: 'Mile Run Time', value: 'mile_run_time' },
  { label: 'Sprint Time', value: 'sprint_time' },
  { label: 'Flexibility (Sit-and-Reach)', value: 'flexibility_sit_and_reach' },
  { label: 'Steps per Day', value: 'steps_per_day' },
  { label: 'Active Minutes', value: 'active_minutes' },
  { label: 'Sleep Quality Score', value: 'sleep_quality_score' },
  { label: 'Energy Level', value: 'energy_level' },
  { label: 'Stress Level', value: 'stress_level' },
  { label: 'Mood Score', value: 'mood_score' },
  { label: 'Blood Glucose', value: 'blood_glucose' },
  { label: 'Body Temperature', value: 'body_temperature' },
] as const;

// =============================================================================
// USER GOAL OPTIONS
// =============================================================================

/**
 * User goal type options
 */
export const USER_GOAL_TYPE_OPTIONS = [
  { value: 'weight', label: 'Weight' },
  { value: 'body_fat_percentage', label: 'Body Fat Percentage' },
  { value: 'strength_gain', label: 'Strength Gain' },
  { value: 'endurance', label: 'Endurance' },
  { value: 'workout_frequency', label: 'Workout Frequency ' },
] as const;

/**
 * User goal metric options
 */
export const USER_GOAL_METRIC_OPTIONS = [
  { value: 'exercise_1rm', label: 'Exercise 1RM' },
  { value: 'body_weight', label: 'Body Weight' },
  { value: 'body_fat_percentage', label: 'Body Fat Percentage' },
  { value: 'run_time', label: 'Run / Cycle Time' },
  { value: 'distance', label: 'Distance' },
  { value: 'spin_time', label: 'Spin Time' },
  { value: 'sessions_per_week', label: 'Sessions Per Week' },
] as const;

/**
 * User goal direction options
 */
export const USER_GOAL_DIRECTION_OPTIONS = [
  { value: 'increase', label: 'Increase' },
  { value: 'decrease', label: 'Decrease' },
] as const;

// =============================================================================
// CHALLENGE OPTIONS
// =============================================================================

/**
 * User goal type options
 */
export const CHALLENGE_TYPE_OPTIONS = [
  { value: 'plan_based', label: 'Plan Based' },
  { value: 'workout_based', label: 'Workout Based' },
  { value: 'goal_based', label: 'Goal Based' },
] as const;

// =============================================================================
// TIMEZONE OPTIONS
// =============================================================================

/**
 * Common timezone options for user profiles
 */
export const TIMEZONE_OPTIONS = [
  { value: '', label: 'Select timezone (optional)' },
  { value: 'UTC', label: 'UTC' },
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'Europe/London', label: 'London (GMT)' },
  { value: 'Europe/Paris', label: 'Paris (CET)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST)' },
] as const;

// =============================================================================
// MEASUREMENT SYSTEM OPTIONS
// =============================================================================

/**
 * Measurement system options for user profiles
 */
export const MEASUREMENT_SYSTEM_OPTIONS = [
  { value: 'metric', label: 'Metric (kg, cm)' },
  { value: 'imperial', label: 'Imperial (lbs, ft/in)' },
] as const;

// =============================================================================
// LOCALE OPTIONS
// =============================================================================

/**
 * Common locale options for user profiles
 */
export const LOCALE_OPTIONS = [
  { value: '', label: 'Select language (optional)' },
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Español' },
  { value: 'fr', label: 'Français' },
  { value: 'de', label: 'Deutsch' },
  { value: 'it', label: 'Italiano' },
  { value: 'pt', label: 'Português' },
  { value: 'ru', label: 'Русский' },
  { value: 'ja', label: '日本語' },
  { value: 'ko', label: '한국어' },
  { value: 'zh', label: '中文' },
] as const;

// =============================================================================
// DASHBOARD PROFILE OPTIONS
// =============================================================================

/**
 * Dashboard profile options for client view
 */
export const DASHBOARD_PROFILE_OPTIONS = [
  {
    value: 'default',
    label: 'Overview',
    description:
      'A balanced dashboard showing your overall fitness progress and recent activity — ideal for everyday use.',
  },
  {
    value: 'social',
    label: 'Social Progress',
    description:
      'See your progress alongside your friends. Compare stats, celebrate milestones, and stay motivated together.',
  },
  {
    value: 'analytics',
    label: 'Performance Insights',
    description:
      'Detailed insights and trends with personalized recommendations for improvement.',
  },
  {
    value: 'compact',
    label: 'Quick View',
    description:
      'A clean, minimal view showing only key metrics — perfect for a quick snapshot of your progress.',
  },
] as const;

/**
 * Dashboard profile options for trainer view
 */
export const TRAINER_DASHBOARD_PROFILE_OPTIONS = [
  {
    value: 'trainer',
    label: 'Client Overview',
    description:
      "A streamlined dashboard for monitoring clients' progress with quick stats and performance highlights.",
  },
] as const;

// =============================================================================
// PREFERRED WORKOUT DAYS OPTIONS
// =============================================================================

/**
 * Preferred workout days options for user preferences
 */
export const PREFERRED_WORKOUT_DAYS_OPTIONS = [
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
  { value: 'sunday', label: 'Sunday' },
] as const;

// =============================================================================
// THEME SELECTION OPTIONS
// =============================================================================

/**
 * Theme selection options for user preferences
 */
export const THEME_SELECTION_OPTIONS = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'auto', label: 'Auto' },
] as const;

// =============================================================================
// WORKOUT OPTIONS
// =============================================================================

/**
 * Body part options
 */
export const BODY_PART_OPTIONS = [
  { value: 'back', label: 'Back' },
  { value: 'chest', label: 'Chest' },
  { value: 'lower arms', label: 'Lower Arms' },
  { value: 'lower legs', label: 'Lower Legs' },
  { value: 'neck', label: 'Neck' },
  { value: 'shoulders', label: 'Shoulders' },
  { value: 'upper arms', label: 'Upper Arms' },
  { value: 'upper legs', label: 'Upper Legs' },
  { value: 'waist', label: 'Waist' },
] as const;

/**
 * Muscle group options
 */
export const MUSCLE_GROUP_OPTIONS = [
  { value: 'abductors', label: 'Abductors' },
  { value: 'abs', label: 'Abs' },
  { value: 'adductors', label: 'Adductors' },
  { value: 'biceps', label: 'Biceps' },
  { value: 'calves', label: 'Calves' },
  { value: 'cardiovascular system', label: 'Cardiovascular System' }, // ?
  { value: 'delts', label: 'Delts' },
  { value: 'forearms', label: 'Forearms' },
  { value: 'glutes', label: 'Glutes' },
  { value: 'hamstrings', label: 'Hamstrings' },
  { value: 'lats', label: 'Lats' },
  { value: 'levator scapulae', label: 'Leverator Scapulae' },
  { value: 'pectorals', label: 'Pectorals' },
  { value: 'quads', label: 'Quads' },
  { value: 'serratus anterior', label: 'Serratus Anterior' },
  { value: 'spine', label: 'Spine' },
  { value: 'traps', label: 'Traps' },
  { value: 'triceps', label: 'Triceps' },
  { value: 'upper back', label: 'Upper Back' },
] as const;

/**
 * Difficulty level options
 */
export const DIFFICULTY_LEVEL_OPTIONS = [
  { value: 'easy', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'hard', label: 'Advanced' },
] as const;

/**
 * Equipment options
 */
export const EQUIPMENT_OPTIONS = [
  { value: 'barbell', label: 'Barbell' },
  { value: 'bodyweight', label: 'Bodyweight' },
  { value: 'dumbbell', label: 'Dumbbell' },
  { value: 'kettlebell', label: 'Kettlebell' },
  { value: 'machine', label: 'Machine' },
  { value: 'medicine ball', label: 'Medicine Ball' },
  { value: 'resistance band', label: 'Resistance Band' },
  { value: 'roller', label: 'Roller' },
] as const;

export const EXERCISE_TYPE_OPTIONS = [
  { value: 'reps_only', label: 'Reps Only' },
  { value: 'weight_reps', label: 'Weight & Reps' },
  { value: 'time_based', label: 'Time Based' },
  { value: 'distance_based', label: 'Distance Based' },
  { value: 'time_distance', label: 'Time & Distance' },
  { value: 'weight_time', label: 'Weight & Time' },
] as const;

/**
 * Tracking-type value → label map.
 *
 * Bridges the two representations of a tracking type:
 * - {@link TrackingType} — the snake_case value form (`weight_reps`) used by
 *   validation schemas / `CustomExerciseOutput`.
 * - {@link ExercisePreferenceTrackingType} — the display label form
 *   (`Weight & Reps`) returned by the `/custom-exercise` API.
 *
 * Derived from {@link EXERCISE_TYPE_OPTIONS} so the two stay in lock-step.
 */
export const TRACKING_TYPE_VALUE_TO_LABEL = Object.fromEntries(
  EXERCISE_TYPE_OPTIONS.map((option) => [option.value, option.label])
) as Record<TrackingType, ExercisePreferenceTrackingType>;

/**
 * The inverse of {@link TRACKING_TYPE_VALUE_TO_LABEL}. The API emits labels but
 * the write schemas take values, so anything turning a user's choice back into
 * a request needs this direction.
 */
export const TRACKING_TYPE_LABEL_TO_VALUE = Object.fromEntries(
  EXERCISE_TYPE_OPTIONS.map((option) => [option.label, option.value])
) as Record<ExercisePreferenceTrackingType, TrackingType>;

// =============================================================================
// FITNESS QUESTIONNAIRE OPTIONS
// =============================================================================
// Values mirror the backend `app/Enums/*` (confirmed against
// `StoreFitnessQuestionaireRequest`); labels come from each enum's `label()`.
// The `satisfies` clauses keep the option values in lock-step with the union
// types in `../types`, so a typo or backend change surfaces at compile time.

/**
 * Fitness level options (`current_fitness_level`)
 */
export const FITNESS_LEVEL_OPTIONS = [
  { value: 'beginner', label: 'Beginner' },
  { value: 'intermediate', label: 'Intermediate' },
  { value: 'advanced', label: 'Advanced' },
  { value: 'athlete', label: 'Athlete / Competitor' },
] as const satisfies readonly { value: FitnessLevel; label: string }[];

/**
 * Fitness goal options (`primary_fitness_goals`)
 */
export const FITNESS_GOAL_OPTIONS = [
  { value: 'lose_weight', label: 'Lose weight / body fat' },
  { value: 'build_muscle', label: 'Build muscle / gain strength' },
  { value: 'improve_endurance', label: 'Improve endurance / cardio fitness' },
  { value: 'increase_flexibility', label: 'Increase flexibility or mobility' },
  {
    value: 'improve_mental_health',
    label: 'Improve mental health / reduce stress',
  },
  { value: 'maintain_fitness', label: 'Maintain current fitness' },
  {
    value: 'train_for_event',
    label: 'Train for an event (race, competition, etc.)',
  },
  { value: 'other', label: 'Other' },
] as const satisfies readonly { value: FitnessGoal; label: string }[];

/**
 * Goal timeline options (`goal_timeline`)
 */
export const GOAL_TIMELINE_OPTIONS = [
  { value: '1-3_months', label: '1-3 months' },
  { value: '3-6_months', label: '3-6 months' },
  { value: '6-12_months', label: '6-12 months' },
  { value: 'no_timeline', label: 'No specific timeline' },
] as const satisfies readonly { value: GoalTimeline; label: string }[];

/**
 * Workout frequency options (`current_days_of_exercise`)
 */
export const WORKOUT_FREQUENCY_OPTIONS = [
  { value: '0', label: 'None' },
  { value: '1-2', label: '1-2 days' },
  { value: '3-4', label: '3-4 days' },
  { value: '5+', label: '5+ days' },
] as const satisfies readonly { value: WorkoutFrequency; label: string }[];

/**
 * Time commitment options (`minutes_of_workout`)
 */
export const TIME_COMMITMENT_OPTIONS = [
  { value: '<20', label: 'Less than 20 minutes' },
  { value: '20-30', label: '20-30 minutes' },
  { value: '30-45', label: '30-45 minutes' },
  { value: '>45', label: 'More than 45 minutes' },
] as const satisfies readonly { value: TimeCommitment; label: string }[];

/**
 * Workout type options (`favourite_workouts`).
 * Excludes `unknown` ('Not sure yet') — the form rejects it (`->except(UNKNOWN)`).
 */
export const WORKOUT_TYPE_OPTIONS = [
  { value: 'strength', label: 'Strength training' },
  { value: 'cardio', label: 'Cardio (running, cycling, HIIT)' },
  { value: 'yoga', label: 'Yoga / Pilates' },
  { value: 'mobility', label: 'Mobility / Stretching' },
  { value: 'sports', label: 'Sports / Outdoor activities' },
  { value: 'calisthenics', label: 'Bodyweight / Calisthenics' },
  { value: 'group', label: 'Group workouts' },
] as const satisfies readonly { value: WorkoutType; label: string }[];

/**
 * Workout location options (`primary_workout_location`)
 */
export const WORKOUT_LOCATION_OPTIONS = [
  { value: 'home', label: 'At home' },
  { value: 'gym', label: 'At the gym' },
  { value: 'outdoors', label: 'Outdoors' },
  { value: 'mixed', label: 'Mix of locations' },
] as const satisfies readonly { value: WorkoutLocation; label: string }[];

/**
 * Equipment access options (`equipment_on_hand`).
 * Distinct from {@link EQUIPMENT_OPTIONS} (what an *exercise* requires) — this is
 * what the *user* has access to. Supports a free-text "Other" via `EnumOrOther`.
 */
export const EQUIPMENT_ACCESS_OPTIONS = [
  { value: 'none', label: 'None / Just bodyweight' },
  { value: 'dumbbells', label: 'Dumbbells' },
  { value: 'resistance_bands', label: 'Resistance bands' },
  { value: 'barbell', label: 'Barbell / Weight plates' },
  { value: 'kettlebells', label: 'Kettlebells' },
  { value: 'cardio_machine', label: 'Treadmill / Cardio machine' },
  { value: 'yoga_mat', label: 'Yoga mat / foam roller' },
  { value: 'other', label: 'Other' },
] as const satisfies readonly { value: EquipmentAccess; label: string }[];

/**
 * Motivation options (`motivation`)
 */
export const MOTIVATION_OPTIONS = [
  { value: 'mental_health', label: 'Feeling better mentally' },
  { value: 'physical_appearance', label: 'Looking better physically' },
  { value: 'medical_reasons', label: 'Medical/health reasons' },
  { value: 'energy_productivity', label: 'Energy / productivity' },
  { value: 'accountability', label: 'Accountability / structure' },
  { value: 'other', label: 'Other' },
] as const satisfies readonly { value: Motivation; label: string }[];

/**
 * Biggest challenge options (`biggest_challenge`)
 */
export const FITNESS_CHALLENGE_OPTIONS = [
  { value: 'consistency', label: 'Staying consistent' },
  { value: 'lack_of_time', label: 'Lack of time' },
  { value: 'not_knowing_what_to_do', label: 'Not knowing what to do' },
  {
    value: 'boredom_or_lack_of_motivation',
    label: 'Boredom / lack of motivation',
  },
  { value: 'injuries_or_limitations', label: 'Injuries or limitations' },
  { value: 'other', label: 'Other' },
] as const satisfies readonly { value: FitnessChallenge; label: string }[];

/**
 * Gender options for the fitness questionnaire (`gender`).
 * Excludes `prefer_not_to_say` — the form rejects it (`->except(PREFER_NOT_TO_SAY)`);
 * leaving the (nullable) field blank is the opt-out.
 */
export const QUESTIONNAIRE_GENDER_OPTIONS = [
  { value: 'female', label: 'Female' },
  { value: 'male', label: 'Male' },
  { value: 'non_binary', label: 'Non-binary' },
  { value: 'transgender', label: 'Transgender' },
  { value: 'genderqueer', label: 'Genderqueer' },
  { value: 'agender', label: 'Agender' },
  { value: 'bigender', label: 'Bigender' },
  { value: 'gender_fluid', label: 'Gender Fluid' },
  { value: 'intersex', label: 'Intersex' },
  { value: 'other', label: 'Other' },
] as const satisfies readonly { value: Gender; label: string }[];

/**
 * Get fitness level options
 */
export function getFitnessLevelOptions(): typeof FITNESS_LEVEL_OPTIONS {
  return FITNESS_LEVEL_OPTIONS;
}

/**
 * Get fitness goal options
 */
export function getFitnessGoalOptions(): typeof FITNESS_GOAL_OPTIONS {
  return FITNESS_GOAL_OPTIONS;
}

/**
 * Get goal timeline options
 */
export function getGoalTimelineOptions(): typeof GOAL_TIMELINE_OPTIONS {
  return GOAL_TIMELINE_OPTIONS;
}

/**
 * Get workout frequency options
 */
export function getWorkoutFrequencyOptions(): typeof WORKOUT_FREQUENCY_OPTIONS {
  return WORKOUT_FREQUENCY_OPTIONS;
}

/**
 * Get time commitment options
 */
export function getTimeCommitmentOptions(): typeof TIME_COMMITMENT_OPTIONS {
  return TIME_COMMITMENT_OPTIONS;
}

/**
 * Get workout type options
 */
export function getWorkoutTypeOptions(): typeof WORKOUT_TYPE_OPTIONS {
  return WORKOUT_TYPE_OPTIONS;
}

/**
 * Get workout location options
 */
export function getWorkoutLocationOptions(): typeof WORKOUT_LOCATION_OPTIONS {
  return WORKOUT_LOCATION_OPTIONS;
}

/**
 * Get equipment access options
 */
export function getEquipmentAccessOptions(): typeof EQUIPMENT_ACCESS_OPTIONS {
  return EQUIPMENT_ACCESS_OPTIONS;
}

/**
 * Get motivation options
 */
export function getMotivationOptions(): typeof MOTIVATION_OPTIONS {
  return MOTIVATION_OPTIONS;
}

/**
 * Get biggest challenge options
 */
export function getFitnessChallengeOptions(): typeof FITNESS_CHALLENGE_OPTIONS {
  return FITNESS_CHALLENGE_OPTIONS;
}

/**
 * Get gender options for the fitness questionnaire (excludes prefer_not_to_say)
 */
export function getQuestionnaireGenderOptions(): typeof QUESTIONNAIRE_GENDER_OPTIONS {
  return QUESTIONNAIRE_GENDER_OPTIONS;
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Get role options for select elements
 */
export function getRoleOptions(): typeof ROLE_OPTIONS {
  return ROLE_OPTIONS;
}

/**
 * Get gender options for registration forms
 */
export function getGenderOptions(): typeof GENDER_OPTIONS {
  return GENDER_OPTIONS;
}

/**
 * Get gender options for profile update forms (includes prefer_not_to_say)
 */
export function getFullGenderOptions(): typeof FULL_GENDER_OPTIONS {
  return FULL_GENDER_OPTIONS;
}

/**
 * Get body measurements options for logging progress
 */
export function getBodyMeasurementOptions(): typeof BODY_MEASUREMENTS {
  return BODY_MEASUREMENTS;
}

/**
 * Get timezone options for select elements
 */
export function getTimezoneOptions(): typeof TIMEZONE_OPTIONS {
  return TIMEZONE_OPTIONS;
}

/**
 * Get measurement system options for select elements
 */
export function getMeasurementSystemOptions(): typeof MEASUREMENT_SYSTEM_OPTIONS {
  return MEASUREMENT_SYSTEM_OPTIONS;
}

/**
 * Get user goal type options for select elements
 */
export function getUserGoalTypeOptions(): typeof USER_GOAL_TYPE_OPTIONS {
  return USER_GOAL_TYPE_OPTIONS;
}

/**
 * Get user goal metric options for select elements
 */
export function getUserGoalMetricOptions(): typeof USER_GOAL_METRIC_OPTIONS {
  return USER_GOAL_METRIC_OPTIONS;
}

/**
 * Get user goal direction options for select elements
 */
export function getUserGoalDirectionOptions(): typeof USER_GOAL_DIRECTION_OPTIONS {
  return USER_GOAL_DIRECTION_OPTIONS;
}

/**
 * Get locale options for select elements
 */
export function getLocaleOptions(): typeof LOCALE_OPTIONS {
  return LOCALE_OPTIONS;
}

/**
 * Get dashboard profile options for client dashboards
 */
export function getDashboardProfileOptions(): typeof DASHBOARD_PROFILE_OPTIONS {
  return DASHBOARD_PROFILE_OPTIONS;
}

/**
 * Get dashboard profile options for trainer dashboards
 */
export function getTrainerDashboardProfileOptions(): typeof TRAINER_DASHBOARD_PROFILE_OPTIONS {
  return TRAINER_DASHBOARD_PROFILE_OPTIONS;
}

/**
 * Get equipment options
 */
export function getEquipmentOptions(): typeof EQUIPMENT_OPTIONS {
  return EQUIPMENT_OPTIONS;
}

/**
 * Get difficulty options
 */
export function getDifficultyOptions(): typeof DIFFICULTY_LEVEL_OPTIONS {
  return DIFFICULTY_LEVEL_OPTIONS;
}

/**
 * Get exercise-type options
 */
export function getExerciseTypeOptions(): typeof EXERCISE_TYPE_OPTIONS {
  return EXERCISE_TYPE_OPTIONS;
}

/**
 * Get body part options
 */
export function getBodyPartOptions(): typeof BODY_PART_OPTIONS {
  return BODY_PART_OPTIONS;
}

/**
 * Get muscle group options
 */
export function getMuscleGroupOptions(): typeof MUSCLE_GROUP_OPTIONS {
  return MUSCLE_GROUP_OPTIONS;
}

/**
 * Get challenge type options
 */
export function getChallengeTypeOptions(): typeof CHALLENGE_TYPE_OPTIONS {
  return CHALLENGE_TYPE_OPTIONS;
}

/**
 * Get preferred workout days options for select elements
 */
export function getPreferredWorkoutDaysOptions(): typeof PREFERRED_WORKOUT_DAYS_OPTIONS {
  return PREFERRED_WORKOUT_DAYS_OPTIONS;
}

/**
 * Get theme selection options for select elements
 */
export function getThemeSelectionOptions(): typeof THEME_SELECTION_OPTIONS {
  return THEME_SELECTION_OPTIONS;
}

/**
 * Find option by value in any options array
 */
export function findOptionByValue<T extends { value: string; label: string }>(
  options: readonly T[],
  value: string
): T | undefined {
  return options.find((option) => option.value === value);
}

/**
 * Get label for a given value from options array
 */
export function getLabelForValue<T extends { value: string; label: string }>(
  options: readonly T[],
  value: string
): string | undefined {
  const option = findOptionByValue(options, value);
  return option?.label;
}

// The API returns and stores gender as a display label ("Non-binary"), but
// forms, the validation schema and the write payload use the enum value
// ("non_binary"). These translate between the two.

/** Enum value for a stored gender label, or `''` when unset or unknown. */
export function genderValueFromLabel(label: string | null | undefined): string {
  if (!label) return '';
  return (
    PROFILE_GENDER_OPTIONS.find((option) => option.label === label)?.value ?? ''
  );
}

/** Display label for a gender enum value, or `undefined` when unset or unknown. */
export function genderLabelFromValue(value: string): string | undefined {
  return getLabelForValue(PROFILE_GENDER_OPTIONS, value);
}

/**
 * Helper function to extract enum values from readonly const arrays
 * while maintaining proper TypeScript tuple types for Zod
 */
export function createEnumFromOptions<T extends readonly { value: string }[]>(
  options: T,
  params?: string | z.core.$ZodEnumParams
): z.ZodEnum<z.core.util.ToEnum<T[number]['value']>> {
  const values = options.map((option) => option.value);
  return z.enum(values as T[number]['value'][], params);
}

// =============================================================================
// TYPE EXPORTS
// =============================================================================

export type RoleOption = (typeof ROLE_OPTIONS)[number];
export type GenderOption = (typeof GENDER_OPTIONS)[number];
export type FullGenderOption = (typeof FULL_GENDER_OPTIONS)[number];
export type TimezoneOption = (typeof TIMEZONE_OPTIONS)[number];
export type MeasurementSystemOption =
  (typeof MEASUREMENT_SYSTEM_OPTIONS)[number];
export type LocaleOption = (typeof LOCALE_OPTIONS)[number];
export type DashboardProfileOption = (typeof DASHBOARD_PROFILE_OPTIONS)[number];
export type TrainerDashboardProfileOption =
  (typeof TRAINER_DASHBOARD_PROFILE_OPTIONS)[number];
export type PreferredWorkoutDayOption =
  (typeof PREFERRED_WORKOUT_DAYS_OPTIONS)[number];
export type ThemeSelectionOption = (typeof THEME_SELECTION_OPTIONS)[number];

export type SelectOption = {
  value: string;
  label: string;
};

export type SelectOptionWithDescription = {
  value: string;
  label: string;
  description: string;
};
